import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Helpers ────────────────────────────────────────────────────────
// Parse YYYY-MM-DD as local date, not UTC.
const parseLocalDate = (val) => {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
};

// Sortable YYYY-MM-DD key in local time.
const toLocalYMD = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "Apr 22" — accepts a YYYY-MM-DD key from toLocalYMD.
const prettyLabel = (ymd) => {
    const d = parseLocalDate(ymd);
    return d
        ? d.toLocaleDateString(undefined, { month: "short", day: "2-digit" })
        : ymd;
};

const OptinDailyAreaChart = ({ optinData }) => {
    // Bucket rows by local date. Backend only returns opt-outs now, so the
    // chart is a single series — no need to stack opt-in vs opt-out.
    const { labels, optOutCounts } = useMemo(() => {
        const map = new Map();
        (optinData || []).forEach((row) => {
            const key = toLocalYMD(row.Date);
            if (!key) return;
            map.set(key, (map.get(key) || 0) + 1);
        });
        // YYYY-MM-DD strings sort lexicographically the same as chronologically.
        const keys = [...map.keys()].sort();
        return {
            labels:       keys.map(prettyLabel),
            optOutCounts: keys.map(k => map.get(k)),
        };
    }, [optinData]);

    if (!optinData?.length) return null;

    const data = {
        labels,
        datasets: [
            {
                label: "Opt-Out",
                data: optOutCounts,
                backgroundColor: "rgba(212,83,126,0.18)",
                borderColor: "#D4537E",
                fill: true,
                tension: 0.35,
                borderWidth: 2,
                pointRadius: 3,
                pointBackgroundColor: "#D4537E",
                pointHoverRadius: 5,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                position: "top",
                labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } },
            },
            tooltip: { enabled: true },
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: {
                beginAtZero: true,
                ticks: { precision: 0, font: { size: 10 } },
                grid: { color: "rgba(0,0,0,0.04)" },
            },
        },
    };

    return (
        <Paper elevation={0} sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, p: "12px 14px", mb: 2,
        }}>
            <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
            }}>
                Opt-outs over time
            </Typography>
            <Box sx={{ height: 260 }}>
                <Line data={data} options={options} />
            </Box>
        </Paper>
    );
};

export default OptinDailyAreaChart;